import { isNumber } from 'lodash'

export const getQuestionById = (questions, id) => {
    if (!questions || !isNumber(id)) return null

    for (const question of questions) {
        if (question.id === id) return question
    }

    return null
}

/**
 * Get a category by ID from questions.
 * Searches both the flattened categories array and rubrics.categories.
 */
export const getCategoryById = (questions, id) => {
    if (!Array.isArray(questions)) return null

    for (const question of questions) {
        if (question.questionRubrics) {
            for (const questionRubric of question.questionRubrics) {
                const rubricCategory = questionRubric.categories?.find(c => c.id === id)
                if (rubricCategory) {
                    return {
                        ...rubricCategory,
                        questionRubricId: questionRubric.id,
                        rubricId: questionRubric.rubricId,
                    }
                }
            }
        }
    }

    return null
}

/**
 * Get a QuestionRubric by ID from questions
 */
export const getQuestionRubricById = (questions, questionRubricId) => {
    if (!Array.isArray(questions) || !isNumber(questionRubricId)) return null

    for (const question of questions) {
        const questionRubric = question.questionRubrics?.find(r => r.id === questionRubricId)
        if (questionRubric) {
            return {
                ...questionRubric,
                questionId: question.id,
                questionName: question.name,
            }
        }
    }

    return null
}

/**
 * Get the parent Question that contains a specific QuestionRubric
 */
export const getQuestionByQuestionRubricId = (questions, questionRubricId) => {
    if (!Array.isArray(questions) || !isNumber(questionRubricId)) return null

    for (const question of questions) {
        const rubric = question.questionRubrics?.find(r => r.id === questionRubricId)
        if (rubric) {
            return question
        }
    }

    return null
}

/**
 * Get all rubrics for a specific question
 */
export const getRubricsByQuestionId = (questions, questionId) => {
    const question = getQuestionById(questions, questionId)
    return question?.rubrics || []
}

const getQuestionPropertyById = (questions, id, property) => {
    const question = getQuestionById(questions, id)
    return question ? question[property] : null
}

export const fetchQuestionsSelectors = {
    byId: (arg, result) => ({
        ...result,
        data: getQuestionById(result?.data, arg.id),
    }),
    earlierQuestions: (arg, result) => ({
        ...result,
        data: result?.data.slice(
            0,
            result?.data?.findIndex(question => question.id === arg.id),
        ),
    }),
    /**
     * Get a Question by QuestionRubric ID (the parent question)
     */
    byQuestionRubricId: (arg, result) => ({
        ...result,
        data: getQuestionByQuestionRubricId(result?.data, arg.questionRubricId),
    }),
    category: (arg, result) => ({
        ...result,
        data: getCategoryById(result?.data, arg.categoryId),
    }),
    /**
     * Get a pattern by ID (alias for category for new architecture)
     */
    pattern: (arg, result) => ({
        ...result,
        data: getCategoryById(result?.data, arg.patternId),
    }),
    /**
     * Get a QuestionRubric by ID
     */
    questionRubric: (arg, result) => ({
        ...result,
        data: getQuestionRubricById(result?.data, arg.questionRubricId),
    }),
    /**
     * Get all rubrics for a question
     */
    rubricsByQuestionId: (arg, result) => ({
        ...result,
        data: getRubricsByQuestionId(result?.data, arg.questionId),
    }),
    propertyById: (arg, result) => ({
        ...result,
        data: getQuestionPropertyById(result?.data, arg.id, arg.property),
    }),
}
